/* ============================================================
   PebbleBloom™ — Shared Reveal
   File: assets/pb-reveal.js

   Drives every scroll entrance in the theme. Pairs with
   assets/pb-motion.css. Replaces the per-section observers that
   were starting to be copied around.

   Contract
     [data-pb-reveal]        container to observe
     [data-pb-reveal-item]   children that fade + rise, staggered
     [data-pb-reveal-wipe]   children that wipe in horizontally

   Progressive enhancement: this file adds .pb-reveal-armed, which
   is what hides the items. No script means no hiding.
   ============================================================ */

(function () {
  'use strict';

  var SELECTOR = '[data-pb-reveal]';

  /* Master switch, set per page type in theme.liquid. Off means no
     entrance motion anywhere on the page - the blanket section fade
     and the per-section stagger both. A page-level switch that
     silenced one but not the other would leave sections half
     animating, which is the state this whole system was untangled
     out of. */
  function pageRevealOff() {
    return document.documentElement.getAttribute('data-pb-section-reveal') === 'off';
  }

  function init(scope) {
    if (pageRevealOff()) return;

    var root = scope && scope.querySelectorAll ? scope : document;
    var targets = root.querySelectorAll(SELECTOR);

    Array.prototype.forEach.call(targets, function (el) {
      if (el.dataset.pbRevealInit === '1') return;
      el.dataset.pbRevealInit = '1';

      // Without IntersectionObserver, leave it un-armed and fully
      // visible rather than entering a hidden state we cannot exit.
      if (!('IntersectionObserver' in window)) return;

      // Index direct item children so CSS can stagger them, unless
      // the section set --i itself.
      var items = el.querySelectorAll('[data-pb-reveal-item]');
      Array.prototype.forEach.call(items, function (item, i) {
        if (!item.style.getPropertyValue('--i')) {
          item.style.setProperty('--i', i);
        }
      });

      el.classList.add('pb-reveal-armed');

      var observer = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('pb-reveal-in');
            obs.unobserve(entry.target);
          });
        },
        // threshold 0 with a negative bottom margin fires once the
        // element is meaningfully on screen, and stays correct when
        // the element is taller than the viewport.
        { threshold: 0, rootMargin: '0px 0px -15% 0px' }
      );

      observer.observe(el);
    });
  }

  /* ---- Site-wide section entrance ----------------------------
     Every top-level section fades up once, so the page has motion
     even where no section opted into the item-level contract.
     The first section is skipped: it is above the fold and would
     animate the buy box out from under the visitor.

     Two ways out of it:

     - A section carrying [data-pb-reveal-off] is skipped. Every pb
       section emits that when its own "Animate On Scroll" box is
       unticked, so one checkbox turns off both the item stagger and
       this blanket fade. Without the marker, unticking the box only
       stopped the stagger and the section still slid up, which read
       as the switch not working.

     - document.documentElement[data-pb-section-reveal="off"] skips
       the lot. Set per template from theme.liquid, for pages whose
       sections have no checkbox of their own. */
  function initSections() {
    if (!('IntersectionObserver' in window)) return;
    if (pageRevealOff()) return;

    var main = document.getElementById('MainContent');
    if (!main) return;

    var sections = main.querySelectorAll(':scope > .shopify-section');

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('pb-sec-in');
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0, rootMargin: '0px 0px -8% 0px' }
    );

    Array.prototype.forEach.call(sections, function (el, i) {
      if (i === 0) return;
      if (el.dataset.pbSecInit === '1') return;
      if (el.querySelector('[data-pb-reveal-off]')) return;
      el.dataset.pbSecInit = '1';
      el.classList.add('pb-sec-armed');
      observer.observe(el);
    });
  }

  /* ---- Ambient loops, only while visible ---------------------
     Independent of the reveal switches: this is not an entrance, it
     is the idle motion, and it should idle whether or not sections
     animate in. Marks each section so pb-motion.css can pause the
     drift outside the part of the page being looked at. Unlike the
     reveal observer this one keeps observing, because a section can
     leave the viewport again.

     The class added is .pb-out-of-view, not .pb-in-view. Marking what
     to pause rather than what to run means that if this never runs -
     no observer, script blocked, anything - the page keeps its
     motion instead of freezing every icon permanently. Same reason
     the reveal system arms from JS rather than Liquid. */
  function initAmbient() {
    if (!('IntersectionObserver' in window)) return;

    var main = document.getElementById('MainContent');
    if (!main) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          entry.target.classList.toggle('pb-out-of-view', !entry.isIntersecting);
        });
      },
      { threshold: 0, rootMargin: '150px 0px' }
    );

    Array.prototype.forEach.call(main.querySelectorAll(':scope > .shopify-section'), function (el) {
      if (el.dataset.pbAmbientInit === '1') return;
      el.dataset.pbAmbientInit = '1';
      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      initSections();
      initAmbient();
    });
  } else {
    init();
    initSections();
    initAmbient();
  }

  // Theme editor re-renders sections on every setting change.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    initAmbient();
  });
})();
